/**
 * BankNodeLiftMatrix — OPC UA node -> lift assignment, per bank.
 *
 * The OPC UA equivalent of BankPinAssignmentMatrix / BankOffsetMatrix. The
 * OPC UA adapter's Mapping is bank-agnostic (it defines WHAT each node
 * means: which LN2 message a node's value maps to), but WHICH lift a node
 * belongs to varies per bank — the same adapter can serve multiple banks,
 * each wired to different physical lifts on the OPC UA server. So node ->
 * lift binding is authored here, per bank, and persisted on the Bank
 * (`opc_ua_node_lift_mapping_binary`, a packed FlatBuffer — see
 * `flatbuffers/builders/opcUaNodeLiftIndexBindingsBuilder.ts`) rather than on
 * the adapter.
 *
 * Rows are the distinct OPC UA nodes referenced anywhere in the adapter's
 * Mapping (nodeToMsg + msgToNode + monitors), deduplicated by their OPC UA
 * `NodeId` string. Each row gets a Select of the bank's lifts; the result is a
 * bank-scoped `Record<nodeIdString, liftId>`.
 *
 * The keys are a wire contract, not a display choice — see
 * `utils/opcUaNodeId.ts`. Nodes with no `NodeId` encoding (ByteString,
 * BrowsePath) cannot be bound at all; they are listed and disabled rather than
 * hidden, because an operator who authored one needs to see why it never
 * produces data.
 */
import React, { useMemo } from "react";
import { Divider, Select, Tag, Tooltip } from "antd";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircleInfo } from "@fortawesome/free-solid-svg-icons";
import { nodeIdSegments, RAW_COLOR } from "../../edgeManagement/adapters/CDO/OPCUA/MappingsEditor";
import { FLOOR_PLACEHOLDER } from "../../edgeManagement/adapters/CDO/OPCUA/floorRange";
import {
  describeUnbindableNode,
  isBindableNode,
  opcUaNodeIdString,
} from "../../../utils/opcUaNodeId";
import type {
  OpcUaNodeIdForm,
  OpcUaNodeToMessageRow,
  OpcUaMessageToNodeRow,
  OpcUaNodeMonitorForm,
} from "../../../interfaces/AdapterCdoOpcUa.interface";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface BankNodeLiftMatrixProps {
  /** The bank's adapter node->message mappings (inbound). */
  nodeToMsg: OpcUaNodeToMessageRow[];
  /** The bank's adapter message->node mappings (outbound). */
  msgToNode: OpcUaMessageToNodeRow[];
  /** The bank's adapter per-node monitors. */
  monitors: OpcUaNodeMonitorForm[];
  /** Lifts/cars in this bank. */
  lifts: { id: string; name: string }[];
  /** Current bank-scoped node -> lift bindings, keyed by `opcUaNodeIdString(node)`. */
  bindings: Record<string, string>;
  /** Called with the FULL updated bindings map on any change. */
  onBindingsChange: (bindings: Record<string, string>) => void;
}

/** One distinct node found in the adapter's mapping, with a stable key + label. */
interface NodeRow {
  key: string;
  label: string;
  /** The node itself, so the row can split it into the same ns / identifier pieces the adapter shows. */
  node: OpcUaNodeIdForm;
  /** False when the node has no OPC UA `NodeId` encoding, so it can never be
   *  bound to a lift. Such rows render disabled with the reason shown. */
  bindable: boolean;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Collect the distinct OPC UA nodes referenced by an adapter's Mapping,
 *  deduplicated by their OPC UA `NodeId` string. Order is stable: nodeToMsg
 *  first, then msgToNode, then monitors, each in array order.
 *
 *  Unbindable nodes are returned too, marked `bindable: false`, so the caller
 *  can show them as unassignable instead of quietly omitting them. */
export function extractDistinctNodes(
  nodeToMsg: OpcUaNodeToMessageRow[],
  msgToNode: OpcUaMessageToNodeRow[],
  monitors: OpcUaNodeMonitorForm[],
): NodeRow[] {
  const seen = new Set<string>();
  const rows: NodeRow[] = [];

  const addNode = (node: OpcUaNodeIdForm | null | undefined): void => {
    if (!node) return;
    // A string node id that still carries `{floor}` belongs to a floor template.
    // LiftCloud expands it per floor and derives each expanded node's lift from
    // the bank's own paths, so there is no single node here to bind to a lift.
    if (node.identifierKind === "string" && node.identifier.includes(FLOOR_PLACEHOLDER)) return;
    const bindable = isBindableNode(node);
    const key = opcUaNodeIdString(node) ?? describeUnbindableNode(node);
    if (seen.has(key)) return;
    seen.add(key);
    rows.push({ key, label: key, node, bindable });
  };

  nodeToMsg.forEach((row) => addNode(row.node));
  msgToNode.forEach((row) => addNode(row.node));
  monitors.forEach((row) => addNode(row.node));

  return rows;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

/**
 * A node read the way the adapter's mapping rows show it: an ns tag and an
 * identifier tag on top, and below them the raw NodeId string with the same
 * colors per section (RAW_COLOR), so a node looks the same here as where it was
 * authored.
 */
function NodeIdCell({ node }: { node: OpcUaNodeIdForm }) {
  const { ns, id } = nodeIdSegments(node);
  return (
    <>
      <div className="generalStyles__textEllipsis">
        {ns !== null && (
          <Tooltip title="NodeId Index">
            <Tag style={{ width: 100, textAlign: "center" }}>{ns}</Tag>
          </Tooltip>
        )}
        <Tooltip title="OPCUA Identifier">
          <Tag style={{ textAlign: "center" }}>{id}</Tag>
        </Tooltip>
      </div>
      <div
        data-testid="node-raw"
        style={{ marginTop: 4, fontFamily: "monospace", fontSize: 12, wordBreak: "break-all" }}
      >
        {ns !== null && (
          <>
            <span style={{ color: RAW_COLOR.ns }}>{ns}</span>
            <span>;</span>
          </>
        )}
        <span style={{ color: RAW_COLOR.id }}>{id}</span>
      </div>
    </>
  );
}

export default function BankNodeLiftMatrix({
  nodeToMsg,
  msgToNode,
  monitors,
  lifts,
  bindings,
  onBindingsChange,
}: BankNodeLiftMatrixProps) {
  const [t] = useTranslation("global");
  const nodeRows = useMemo(
    () => extractDistinctNodes(nodeToMsg, msgToNode, monitors),
    [nodeToMsg, msgToNode, monitors],
  );

  const assignLift = (nodeKey: string, liftId: string): void => {
    onBindingsChange({ ...bindings, [nodeKey]: liftId });
  };

  const clearLift = (nodeKey: string): void => {
    const { [nodeKey]: _removed, ...rest } = bindings;
    onBindingsChange(rest);
  };

  if (nodeRows.length === 0) {
    return (
      <div style={{ color: "#888", padding: 8 }}>
        No OPC UA node mappings defined on the adapter. Configure node-to-message mappings on the
        adapter CDO page first.
      </div>
    );
  }

  const bindableRows = nodeRows.filter((row) => row.bindable);
  const unbindableCount = nodeRows.length - bindableRows.length;
  const assignedCount = bindableRows.filter((row) => bindings[row.key]).length;

  return (
    <div>
      <Divider orientation="left" plain>
        Node → Lift Assignment ({assignedCount} of {bindableRows.length} assigned)
      </Divider>

      {unbindableCount > 0 && (
        <div style={{ color: "#d89614", fontSize: 12, padding: "0 12px 8px" }}>
          {unbindableCount} node{unbindableCount === 1 ? "" : "s"} cannot be assigned to a lift.
          ByteString and BrowsePath nodes have no OPC UA NodeId string, which is what a bank binding
          is keyed by. Re-author them as Numeric, String or Guid nodes to bind them.
        </div>
      )}

      {/* Header */}
      <div
        className="row"
        style={{
          fontWeight: 600,
          fontSize: 11,
          color: "#888",
          padding: "4px 12px",
          borderBottom: "1px solid #303030",
          marginBottom: 4,
        }}
      >
        <div className="col-8">
          OPC UA Node
          <Tooltip title={t("general.bankNodeLiftNodeHelp")}>
            <FontAwesomeIcon icon={faCircleInfo} className="generalStyles__mlFix" />
          </Tooltip>
        </div>
        <div className="col-4">
          Lift / Car
          <Tooltip title={t("general.bankNodeLiftLiftHelp")}>
            <FontAwesomeIcon icon={faCircleInfo} className="generalStyles__mlFix" />
          </Tooltip>
        </div>
      </div>

      {nodeRows.map((row) => (
        <div
          key={row.key}
          className="row"
          style={{
            padding: "6px 12px",
            borderBottom: "1px solid #1f1f1f",
            alignItems: "center",
          }}
        >
          <div className="col-8">
            <NodeIdCell node={row.node} />
          </div>
          <div className="col-4">
            <Select
              className="generalStyles__width100"
              size="small"
              disabled={!row.bindable}
              value={row.bindable ? bindings[row.key] || undefined : undefined}
              placeholder={row.bindable ? "Unassigned" : "Not bindable"}
              onChange={(val: string) => assignLift(row.key, val)}
              allowClear
              onClear={() => clearLift(row.key)}
            >
              {lifts.map((l) => (
                <Select.Option key={l.id} value={l.id}>
                  {l.name}
                </Select.Option>
              ))}
            </Select>
          </div>
        </div>
      ))}
    </div>
  );
}
